import { ChartDataPoint } from 'features/systemLens'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import { UPlotSeriesConfig } from 'features/timeSeries/utils/uPlotData'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import uPlot from 'uplot'

export interface RulerSeriesValue {
  sensorId: string
  name: string
  color: string
  yValue: number | null
  unit?: string
  /** CSS pixel offset relative to u.over top edge, for nearest-series detection */
  yPixelInPlot: number | null
}

export interface RulerHoverState {
  /** CSS pixel offset from the left edge of the overlay div */
  xPixelOnOverlay: number
  /** Unix timestamp (ms) at the cursor position */
  xValue: number
  seriesValues: RulerSeriesValue[]
  nearestSensorId: string | null
}

export interface RulerSelection {
  /** Left edge in CSS pixels relative to the overlay div */
  startPixelOnOverlay: number
  /** Right edge in CSS pixels relative to the overlay div */
  endPixelOnOverlay: number
  startXValue: number
  endXValue: number
}

export interface Ruler extends RulerSelection {
  id: string
  selectedSensorIds: string[]
  isMinimized: boolean
  /** User-applied vertical offset for the metrics panel, in CSS pixels */
  panelOffsetY: number
}

export interface RulerMetrics {
  min: number | null
  max: number | null
  diff: number | null
  mean: number | null
  minTime: number | null
  maxTime: number | null
  unit?: string
  count: number
}

export interface VisibleSensorInfo {
  sensorId: string
  name: string
  color: string
}

interface UseRulerToolProps {
  uPlotRef: React.RefObject<uPlot | null>
  chartData: ChartDataPoint[]
  unitMapping: Record<string, string>
  sensorRanges: Record<string, { min?: number; max?: number; unit: string }>
  allSensorsToRender: Array<{ id: string; name: string }>
  customSensorColors?: Record<string, string>
  isActive: boolean
  /**
   * Ruler state is owned by `ScatterplotProvider`, not by this hook, so that
   * saving a chart can see it and loading one can restore it. The hook stays the
   * only thing that *mutates* it.
   */
  rulers: Ruler[]
  setRulers: React.Dispatch<React.SetStateAction<Ruler[]>>
}

export interface ProjectedRulerSpan {
  startPx: number
  endPx: number
  horizontallyVisible: boolean
}

/**
 * Project a ruler's persisted x-domain values to on-overlay pixels.
 *
 * The stored form carries only `startXValue`/`endXValue`; the pixel fields are
 * viewport-derived and rehydrate to 0 on load (see `loadRulerConfigsFromChart`).
 * So a freshly loaded ruler is only visible *once plot bounds exist* and its
 * x-values can be projected via `valToPos` — until then it collapses to a
 * zero-width span at pixel 0. That is exactly why the overlay must recompute
 * `plotBounds` when uPlot becomes available on the load path (Bug 2).
 *
 * Pass `valToPos`/`bounds` as null when uPlot is not yet mounted; the ruler then
 * falls back to its (zero) stored pixels, matching the pre-bounds render.
 */
export function projectRulerSpan(
  ruler: Pick<
    Ruler,
    'startXValue' | 'endXValue' | 'startPixelOnOverlay' | 'endPixelOnOverlay'
  >,
  valToPos: ((value: number, scale: string) => number) | null,
  bounds: { overLeft: number; overRight: number } | null
): ProjectedRulerSpan {
  const rawStartPx =
    valToPos && bounds
      ? valToPos(ruler.startXValue, 'x') + bounds.overLeft
      : ruler.startPixelOnOverlay
  const rawEndPx =
    valToPos && bounds
      ? valToPos(ruler.endXValue, 'x') + bounds.overLeft
      : ruler.endPixelOnOverlay

  const startPx = Number.isFinite(rawStartPx)
    ? rawStartPx
    : ruler.startPixelOnOverlay
  const endPx = Number.isFinite(rawEndPx) ? rawEndPx : ruler.endPixelOnOverlay

  const horizontallyVisible =
    bounds !== null
      ? !(endPx < bounds.overLeft || startPx > bounds.overRight)
      : true

  return { startPx, endPx, horizontallyVisible }
}

/**
 * Next `${prefix}-${n}` id given the ids already in use.
 *
 * Derived from current state rather than a mount-scoped counter: a counter
 * resets to 0 on mount, so restoring a saved `ruler-1` and then drawing a new
 * ruler would mint `ruler-1` again and collide.
 */
export function nextToolId(existingIds: string[], prefix: string): string {
  let maxSuffix = 0
  for (const id of existingIds) {
    if (!id.startsWith(`${prefix}-`)) continue
    const suffix = Number(id.slice(prefix.length + 1))
    if (Number.isInteger(suffix) && suffix > maxSuffix) maxSuffix = suffix
  }
  return `${prefix}-${maxSuffix + 1}`
}

export function findNearestIndex(
  xs: ArrayLike<number>,
  target: number
): number {
  let lo = 0
  let hi = xs.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if ((xs[mid] as number) < target) lo = mid + 1
    else hi = mid
  }
  if (
    lo > 0 &&
    Math.abs((xs[lo - 1] as number) - target) <
      Math.abs((xs[lo] as number) - target)
  ) {
    return lo - 1
  }
  return lo
}

export function getSeriesInfoFromUPlot(u: uPlot): Array<{
  dataIndex: number
  sensorId: string
  name: string
  color: string
  show: boolean
  scale: string
  isRegression: boolean
}> {
  return u.series.slice(1).map((s, i) => {
    const config = s as UPlotSeriesConfig
    return {
      dataIndex: i + 1,
      sensorId: config._sensorId ?? `series_${i}`,
      name: typeof config.label === 'string' ? config.label : `Series ${i + 1}`,
      color: typeof config.stroke === 'string' ? config.stroke : '#ffffff',
      show: config.show !== false,
      scale: config.scale ?? 'y',
      isRegression: config._isRegression ?? false,
    }
  })
}

export function useRulerTool({
  uPlotRef,
  chartData,
  unitMapping,
  sensorRanges,
  allSensorsToRender,
  customSensorColors,
  isActive,
  rulers,
  setRulers,
}: UseRulerToolProps) {
  /** Canonical color — same formula as buildAlignedColumns in uPlotData.ts */
  const getSensorColorById = useCallback(
    (sensorId: string): string => {
      if (customSensorColors?.[sensorId]) return customSensorColors[sensorId]
      const index = allSensorsToRender.findIndex((s) => s.id === sensorId)
      return getSensorColor(index >= 0 ? index : 0)
    },
    [allSensorsToRender, customSensorColors]
  )

  const [hoverState, setHoverState] = useState<RulerHoverState | null>(null)
  const [draft, setDraft] = useState<RulerSelection | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [resizingEdge, setResizingEdge] = useState<'start' | 'end' | null>(null)
  const [activeRulerId, setActiveRulerId] = useState<string | null>(null)
  const dragStartRef = useRef<{ pixelX: number; xValue: number } | null>(null)
  const lastVisibleSensorsRef = useRef<VisibleSensorInfo[]>([])

  const resetTransientState = useCallback(() => {
    setHoverState(null)
    setIsDragging(false)
    setResizingEdge(null)
    setActiveRulerId(null)
    setDraft(null)
    dragStartRef.current = null
  }, [])

  useEffect(() => {
    if (isActive) return
    resetTransientState()
  }, [isActive, resetTransientState])

  /** Compute plot-area bounds relative to the overlay element */
  function getPlotBoundsRelativeToOverlayLocal(
    overlayEl: HTMLElement,
    u: uPlot
  ) {
    return getPlotBoundsRelativeToOverlay(overlayEl, u)
  }

  const buildHoverState = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, u: uPlot): RulerHoverState | null => {
      const overlayEl = e.currentTarget
      const overlayRect = overlayEl.getBoundingClientRect()
      const overRect = u.over.getBoundingClientRect()

      const overLeft = overRect.left - overlayRect.left
      const overRight = overLeft + overRect.width
      const overTop = overRect.top - overlayRect.top

      const mouseXOnOverlay = e.clientX - overlayRect.left
      const mouseYOnOverlay = e.clientY - overlayRect.top

      if (mouseXOnOverlay < overLeft || mouseXOnOverlay > overRight) return null

      const clampedX = Math.max(overLeft, Math.min(overRight, mouseXOnOverlay))
      const relXInOver = clampedX - overLeft
      const xValue = u.posToVal(relXInOver, 'x')
      if (xValue === null || xValue === undefined || isNaN(xValue)) return null

      const xArr = u.data[0]
      if (!xArr || xArr.length === 0) return null
      const nearestIdx = findNearestIndex(xArr as number[], xValue)

      const mouseYRelativeToOver = mouseYOnOverlay - overTop
      const allSeries = getSeriesInfoFromUPlot(u)

      let nearestSensorId: string | null = null
      let minDist = Infinity

      const seriesValues: RulerSeriesValue[] = allSeries
        .filter((s) => !s.isRegression && s.show)
        .map((s) => {
          const yDataArr = u.data[s.dataIndex] as
            | ArrayLike<number | null>
            | undefined
          const yValue =
            yDataArr !== null && yDataArr !== undefined
              ? ((yDataArr[nearestIdx] as number | null | undefined) ?? null)
              : null

          let yPixelInPlot: number | null = null
          if (yValue !== null) {
            try {
              yPixelInPlot = u.valToPos(yValue, s.scale)
            } catch {
              yPixelInPlot = null
            }
          }

          if (yPixelInPlot !== null) {
            const dist = Math.abs(yPixelInPlot - mouseYRelativeToOver)
            if (dist < minDist) {
              minDist = dist
              nearestSensorId = s.sensorId
            }
          }

          return {
            sensorId: s.sensorId,
            name: s.name,
            color: getSensorColorById(s.sensorId),
            yValue,
            unit: unitMapping[s.sensorId],
            yPixelInPlot,
          }
        })

      return {
        xPixelOnOverlay: clampedX,
        xValue,
        seriesValues,
        nearestSensorId,
      }
    },
    [unitMapping, getSensorColorById]
  )

  const handleOverlayMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const u = uPlotRef.current
      if (!u) return

      const overlayEl = e.currentTarget
      const { overLeft, overRight } = getPlotBoundsRelativeToOverlayLocal(
        overlayEl,
        u
      )
      const mouseXOnOverlay = e.clientX - overlayEl.getBoundingClientRect().left

      // Edge resize mode — update only the dragged edge of the active ruler, swap if crossed
      if (isDragging && resizingEdge && activeRulerId) {
        const target = rulers.find((r) => r.id === activeRulerId)
        if (!target) return

        const clampedX = Math.max(
          overLeft,
          Math.min(overRight, mouseXOnOverlay)
        )
        const newVal = u.posToVal(clampedX - overLeft, 'x')

        if (resizingEdge === 'start') {
          if (clampedX >= target.endPixelOnOverlay) {
            // Crossed past the end — swap edges
            setRulers((prev) =>
              prev.map((r) =>
                r.id === activeRulerId
                  ? {
                      ...r,
                      startPixelOnOverlay: r.endPixelOnOverlay,
                      endPixelOnOverlay: clampedX,
                      startXValue: r.endXValue,
                      endXValue: newVal,
                    }
                  : r
              )
            )
            setResizingEdge('end')
          } else {
            setRulers((prev) =>
              prev.map((r) =>
                r.id === activeRulerId
                  ? {
                      ...r,
                      startPixelOnOverlay: clampedX,
                      startXValue: newVal,
                    }
                  : r
              )
            )
          }
        } else {
          if (clampedX <= target.startPixelOnOverlay) {
            // Crossed past the start — swap edges
            setRulers((prev) =>
              prev.map((r) =>
                r.id === activeRulerId
                  ? {
                      ...r,
                      startPixelOnOverlay: clampedX,
                      endPixelOnOverlay: r.startPixelOnOverlay,
                      startXValue: newVal,
                      endXValue: r.startXValue,
                    }
                  : r
              )
            )
            setResizingEdge('start')
          } else {
            setRulers((prev) =>
              prev.map((r) =>
                r.id === activeRulerId
                  ? {
                      ...r,
                      endPixelOnOverlay: clampedX,
                      endXValue: newVal,
                    }
                  : r
              )
            )
          }
        }

        return // no hover state update during edge resize
      }

      // New-selection drag mode
      if (isDragging && dragStartRef.current) {
        const clampedX = Math.max(
          overLeft,
          Math.min(overRight, mouseXOnOverlay)
        )
        const relXInOver = clampedX - overLeft
        const xValue = u.posToVal(relXInOver, 'x')

        const start = dragStartRef.current
        const startX = Math.min(start.pixelX, clampedX)
        const endX = Math.max(start.pixelX, clampedX)
        const startXVal = Math.min(start.xValue, xValue)
        const endXVal = Math.max(start.xValue, xValue)

        setDraft({
          startPixelOnOverlay: startX,
          endPixelOnOverlay: endX,
          startXValue: startXVal,
          endXValue: endXVal,
        })
      }

      setHoverState(buildHoverState(e, u))
    },
    [
      uPlotRef,
      isDragging,
      resizingEdge,
      activeRulerId,
      rulers,
      buildHoverState,
      setRulers,
    ]
  )

  const handleOverlayMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      const u = uPlotRef.current
      if (!u) return

      const overlayEl = e.currentTarget
      const { overLeft, overRight } = getPlotBoundsRelativeToOverlayLocal(
        overlayEl,
        u
      )
      const mouseXOnOverlay = e.clientX - overlayEl.getBoundingClientRect().left

      if (mouseXOnOverlay < overLeft || mouseXOnOverlay > overRight) return

      const clampedX = Math.max(overLeft, Math.min(overRight, mouseXOnOverlay))
      const relXInOver = clampedX - overLeft
      const xValue = u.posToVal(relXInOver, 'x')

      dragStartRef.current = { pixelX: clampedX, xValue }
      setIsDragging(true)
      setResizingEdge(null)
      setActiveRulerId(null)
      setDraft(null)
      e.preventDefault()
    },
    [uPlotRef]
  )

  const handleEdgeMouseDown = useCallback(
    (rulerId: string, edge: 'start' | 'end', e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      setActiveRulerId(rulerId)
      setResizingEdge(edge)
      setIsDragging(true)
    },
    []
  )

  const handleOverlayMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging) return

      // Edge resize — just finalize, no new ruler
      if (resizingEdge) {
        setResizingEdge(null)
        setActiveRulerId(null)
        setIsDragging(false)
        return
      }

      if (!dragStartRef.current) return
      const u = uPlotRef.current
      if (!u) return

      const overlayEl = e.currentTarget
      const { overLeft, overRight } = getPlotBoundsRelativeToOverlayLocal(
        overlayEl,
        u
      )
      const mouseXOnOverlay = e.clientX - overlayEl.getBoundingClientRect().left
      const clampedX = Math.max(overLeft, Math.min(overRight, mouseXOnOverlay))
      const relXInOver = clampedX - overLeft
      const xValue = u.posToVal(relXInOver, 'x')

      const start = dragStartRef.current
      const startX = Math.min(start.pixelX, clampedX)
      const endX = Math.max(start.pixelX, clampedX)
      const startXVal = Math.min(start.xValue, xValue)
      const endXVal = Math.max(start.xValue, xValue)

      if (endX - startX > 5) {
        const visible = getSeriesInfoFromUPlot(u).filter(
          (s) => s.show && !s.isRegression
        )
        const initialSensorIds = visible.length > 0 ? [visible[0].sensorId] : []

        // Mint the id from `prev` so restored rulers are taken into account.
        setRulers((prev) => [
          ...prev,
          {
            id: nextToolId(
              prev.map((r) => r.id),
              'ruler'
            ),
            startPixelOnOverlay: startX,
            endPixelOnOverlay: endX,
            startXValue: startXVal,
            endXValue: endXVal,
            selectedSensorIds: initialSensorIds,
            isMinimized: false,
            panelOffsetY: 0,
          },
        ])
      }

      setDraft(null)
      setIsDragging(false)
      dragStartRef.current = null
    },
    [isDragging, resizingEdge, uPlotRef, setRulers]
  )

  const handleOverlayMouseLeave = useCallback(() => {
    setHoverState(null)
    if (isDragging) {
      setResizingEdge(null)
      setActiveRulerId(null)
      setIsDragging(false)
      setDraft(null)
      dragStartRef.current = null
    }
  }, [isDragging])

  const computeMetrics = useCallback(
    (
      sensorId: string,
      startXValue: number,
      endXValue: number
    ): RulerMetrics => {
      const unit =
        unitMapping[sensorId] || sensorRanges[sensorId]?.unit || undefined
      const filtered = chartData.filter(
        (d) => d.timestamp >= startXValue && d.timestamp <= endXValue
      )

      const values: Array<{ value: number; timestamp: number }> = []
      for (const point of filtered) {
        const v = point[sensorId]
        if (typeof v === 'number' && !isNaN(v)) {
          values.push({ value: v, timestamp: point.timestamp })
        }
      }

      if (values.length === 0) {
        return {
          min: null,
          max: null,
          diff: null,
          mean: null,
          minTime: null,
          maxTime: null,
          unit,
          count: 0,
        }
      }

      let min = values[0].value
      let max = values[0].value
      let minTime = values[0].timestamp
      let maxTime = values[0].timestamp
      let sum = 0

      for (const { value, timestamp } of values) {
        if (value < min) {
          min = value
          minTime = timestamp
        }
        if (value > max) {
          max = value
          maxTime = timestamp
        }
        sum += value
      }

      return {
        min,
        max,
        diff: max - min,
        mean: sum / values.length,
        minTime,
        maxTime,
        unit,
        count: values.length,
      }
    },
    [chartData, unitMapping, sensorRanges]
  )

  const getVisibleSensors = useCallback((): VisibleSensorInfo[] => {
    const u = uPlotRef.current
    if (!u) return lastVisibleSensorsRef.current
    const visibleSensors = getSeriesInfoFromUPlot(u)
      .filter((s) => s.show && !s.isRegression)
      .map((s) => ({
        sensorId: s.sensorId,
        name: s.name,
        color: getSensorColorById(s.sensorId),
      }))
    lastVisibleSensorsRef.current = visibleSensors
    return visibleSensors
  }, [uPlotRef, getSensorColorById])

  const removeRuler = useCallback(
    (id: string) => {
      setRulers((prev) => prev.filter((r) => r.id !== id))
    },
    [setRulers]
  )

  const setRulerSensorIds = useCallback(
    (id: string, ids: string[]) => {
      setRulers((prev) =>
        prev.map((r) => (r.id === id ? { ...r, selectedSensorIds: ids } : r))
      )
    },
    [setRulers]
  )

  const setRulerMinimized = useCallback(
    (id: string, isMinimized: boolean) => {
      setRulers((prev) =>
        prev.map((r) => (r.id === id ? { ...r, isMinimized } : r))
      )
    },
    [setRulers]
  )

  const setRulerPanelOffsetY = useCallback(
    (id: string, panelOffsetY: number) => {
      setRulers((prev) =>
        prev.map((r) => (r.id === id ? { ...r, panelOffsetY } : r))
      )
    },
    [setRulers]
  )

  /** Called by RulerOverlay after zoom/pan to keep stored pixels in sync */
  const updateRulerPixels = useCallback(
    (id: string, startPx: number, endPx: number) => {
      if (!Number.isFinite(startPx) || !Number.isFinite(endPx)) return
      setRulers((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, startPixelOnOverlay: startPx, endPixelOnOverlay: endPx }
            : r
        )
      )
    },
    [setRulers]
  )

  return {
    hoverState,
    rulers,
    draft,
    isDragging,
    resizingEdge,
    activeRulerId,
    handleOverlayMouseMove,
    handleOverlayMouseDown,
    handleEdgeMouseDown,
    handleOverlayMouseUp,
    handleOverlayMouseLeave,
    removeRuler,
    setRulerSensorIds,
    setRulerMinimized,
    setRulerPanelOffsetY,
    updateRulerPixels,
    computeMetrics,
    getVisibleSensors,
  }
}

export function getPlotBoundsRelativeToOverlay(
  overlayEl: HTMLElement,
  u: uPlot
): { overLeft: number; overRight: number; overTop: number } {
  const overlayRect = overlayEl.getBoundingClientRect()
  const overRect = u.over.getBoundingClientRect()
  return {
    overLeft: overRect.left - overlayRect.left,
    overRight: overRect.left - overlayRect.left + overRect.width,
    overTop: overRect.top - overlayRect.top,
  }
}
